import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Deep-link focus. A task notification links to `?focus=<id>`; once the list
 * has loaded, scroll that row into view and flash a ring around it so the
 * reviewer's eye lands on the exact thing that needs a decision.
 *
 * Mark the row with `data-focus-id={id}` and call this with a `ready` flag that
 * flips true when the list has rendered.
 */
export function useFocusTarget(ready) {
  const [params, setParams] = useSearchParams();
  const focus = params.get('focus');

  useEffect(() => {
    if (!focus || !ready) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-focus-id="${focus}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('gv-focus-flash');
      setTimeout(() => el.classList.remove('gv-focus-flash'), 2600);
      // Consume the param so a refresh doesn't re-flash it.
      const next = new URLSearchParams(params);
      next.delete('focus');
      setParams(next, { replace: true });
    }, 260);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, ready]);

  return focus;
}

export default useFocusTarget;
